import { Icon } from '@iconify/react';
import { formatToBRL } from 'base/src/utils/mathUtil';
import { formatDate } from 'base/src/utils/stringUtil';
import styled from 'styled-components';

export interface Boleto {
  id: string;
  description: string;
  dataCriacao: string | Date;
  blobUrl: string;
  blobToken: string; // Add this field
  amount: string;
  status: 'Em aberto' | 'pago' | 'vencido' | 'cancelado' | 'duplicado';
  usuario: string;
}

interface BoletoCardProps {
  boleto: Boleto;
  onClick?: () => void;
  isDuplicated?: boolean;
}

const CardContainer = styled.div<{ clickable?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px;
  background-color: white;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  transition: background-color 0.2s;
  cursor: ${props => props.clickable ? 'pointer' : 'default'};
  margin-bottom: 12px;
  
  &:hover {
    background-color: ${props => props.clickable ? '#f9fafb' : 'white'};
  }
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 16px;
  }
`;

const LeftSection = styled.div`
  display: flex;
  align-items: center;
  flex: 1;
  
  @media (max-width: 768px) {
    width: 100%;
  }
`;

const CenterSection = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;  
  padding-right: 20px; 
  flex: 0.5;
  
  @media (max-width: 768px) {
    width: 100%;
    justify-content: flex-start;
    padding-right: 0;  /* Remove padding on mobile */
    margin: 8px 0;
  }
`;

const IconContainer = styled.div`
  color: #7c3aed;
  margin-right: 16px;
  font-size: 24px;
  background-color: #f3e8ff;
  padding: 8px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const InfoContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const Title = styled.span`
  font-weight: 600;
  font-size: 16px;
  color: #1f2937;
  margin-bottom: 4px;
`;

const UserContainer = styled.div`
  display: flex;
  align-items: center;
  font-size: 14px;
  color: #6b7280;
`;

const UserIcon = styled.span`
  margin-right: 4px;
  display: flex;
  align-items: center;
`;

const RightSection = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  flex: 1;
  
  @media (max-width: 768px) {
    width: 100%;
  }
`;

const StatusTag = styled.span<{ status: string }>`
  padding: 6px 14px;
  border-radius: 16px;
  font-size: 14px;
  font-weight: 500;
  width: 100px;
  text-align: center;
  display: inline-block; 
  background-color: ${({ status }) =>
    status === 'Em aberto' ? '#fef3c7' :
      status === 'pago' ? '#d1fae5' :
        status === 'vencido' ? '#fee2e2' :
          status === 'duplicado' ? '#f0e6ff' :
            '#f3f4f6'};
  color: ${({ status }) =>
    status === 'Em aberto' ? '#92400e' :
      status === 'pago' ? '#065f46' :
        status === 'vencido' ? '#b91c1c' :
          status === 'duplicado' ? '#6d28d9' :
            '#4b5563'};
`;

const Amount = styled.span`
  font-weight: 600;
  font-size: 16px;
  color: #1f2937;
`;

function getStatusText(status: string): string {

  const statusMap = {
    pendente: "Em aberto",
    pago: "Pago",
    vencido: "Vencido",
    cancelado: "Cancelado",
    duplicado: "Duplicado"
  };

  return statusMap[status as keyof typeof statusMap] || "Em aberto";
}

function BoletoCard({ boleto, onClick }: BoletoCardProps) {
  return (
    <CardContainer clickable={!!onClick} onClick={onClick}>
      <LeftSection>
        <IconContainer>
          <Icon icon="akar-icons:file" height="1em" />
        </IconContainer>
        <InfoContainer>
          <Title>Boleto #{formatDate(boleto.dataCriacao)}</Title>
          <UserContainer>
            <UserIcon>
              <Icon icon="mingcute:user-line" height="1.2em" />
            </UserIcon>
            Gerado Por: {boleto.usuario}
          </UserContainer>
        </InfoContainer>
      </LeftSection>

      <CenterSection>
        <StatusTag status={boleto.status}>{getStatusText(boleto.status)}</StatusTag>
      </CenterSection>

      <RightSection>
        <Amount>{formatToBRL(parseFloat(boleto.amount))}</Amount>
      </RightSection>
    </CardContainer>
  );
}

export default BoletoCard;